import AccessControl from '@/components/AccessControl'
import { Stack } from '@/components/custom-components'
import { Button, buttonVariants } from '@/components/shadcn/button.tsx'

import { CreativeOSPages } from '@/_shared/subscriptionPlans'
import InspirationAnalysisDetails from '@/components/InspirationAnalysisDetails'
import { HistoryType, historyColumns } from '@/components/creative-analysis/history/columns'
import { Loader } from '@/components/custom-components/Loader'
import { Badge } from '@/components/shadcn/badge'
import { DataTable } from '@/components/shadcn/data-table'
import { DatePickerWithRange } from '@/components/shadcn/date-picker'
import { useElementSize } from '@/hooks/useElementSize'
import { cn } from '@/lib/utils'
import { trpc } from '@/utils/trpc'
import { ArrowLeft, Plus } from '@mynaui/icons-react'
import { Link, createFileRoute } from '@tanstack/react-router'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useInView } from 'react-intersection-observer'

type SearchParams = {
  sideBarOpen?: boolean
}

export const Route = createFileRoute('/feeds/creative-toolkit/funnel-creative-analyzer/history/')({
  component: All,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const sideBarOpen = search?.sideBarOpen as boolean

    return {
      sideBarOpen,
    }
  },
})

function All() {
  const [squareRef] = useElementSize()
  const [allAnalysisHistory, setAllAnalysisHistory] = useState<HistoryType[]>([])
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined)

  const {
    data: analysisHistory,
    isLoading: isLoadingAnalysisHistory,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = trpc.getFunnelCongruenceHistory.useInfiniteQuery(
    {
      limit: 10,
      filterStart: dateRange?.from?.toISOString() ?? undefined,
      filterEnd: dateRange?.to?.toISOString() ?? undefined,
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      initialCursor: 1,
    },
  )

  const { ref: scrollRef, inView } = useInView({
    threshold: 0,
    trackVisibility: true,
    delay: 100,
    initialInView: false,
  })

  useEffect(() => {
    if (
      inView &&
      hasNextPage &&
      !isFetchingNextPage &&
      analysisHistory &&
      allAnalysisHistory &&
      allAnalysisHistory.length &&
      analysisHistory.pages[analysisHistory.pages.length - 1]?.results.length > 0
    ) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, fetchNextPage, analysisHistory, isFetchingNextPage, allAnalysisHistory])

  useEffect(() => {
    if (!analysisHistory) return

    const uniqueResults = new Map<string, HistoryType>()
    analysisHistory.pages.forEach((page) => {
      page.results.forEach((result) => {
        const parsed = (() => {
          try {
            return JSON.parse(result.analysis)
          } catch {
            return {}
          }
        })()

        const analysis = typeof result.analysis === 'string' ? parsed : result.analysis

        uniqueResults.set(result.taskId, {
          createdAt: result.createdAt,
          images: result.images,
          taskId: result.taskId,
          score: analysis?.coherence_evaluation?.overall_score?.score ?? null,
          analysis: {
            analysis: analysis,
            landing_page_image_data: result.images.find((image) => image.type === 'landing-page')?.fcImageUrl ?? '',
            ad_images_data: result.images.find((image) => image.type === 'ad')?.fcImageUrl,
          },
        })
      })
    })

    setAllAnalysisHistory((prevData) => {
      const newData = Array.from(uniqueResults.values())
      if (JSON.stringify(prevData) === JSON.stringify(newData)) return prevData // Prevent redundant updates
      return newData
    })
  }, [analysisHistory])

  return (
    <>
      <AccessControl page={CreativeOSPages.CREATIVE_TOOLKIT_COPY_GRADER}>
        <Stack ref={squareRef} className="gap-3 lg:gap-6">
          <Stack className="gap-3 lg:gap-8">
            <Stack className="gap-3 lg:gap-6">
              <div>
                <Link
                  className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }))}
                  to={'/feeds/creative-toolkit/funnel-creative-analyzer'}
                >
                  <ArrowLeft /> Back
                </Link>
              </div>
              <div className={'flex flex-col lg:flex-row justify-between gap-5 lg:items-center'}>
                <div className={''}>
                  <h1 className={'text-lg lg:text-2xl font-semibold'}>Your Analysis History</h1>
                  <p className={'text-lg lg:text-base text-thememutedforeground mt-2'}>
                    See all your recent analysis history.
                  </p>
                </div>
                <div>
                  <Link
                    className={cn(
                      buttonVariants({
                        variant: 'destructivePurple',
                        size: 'sm',
                      }),
                    )}
                    to={'/feeds/creative-toolkit/funnel-creative-analyzer'}
                  >
                    <Plus /> Create Analysis
                  </Link>
                </div>
              </div>
              <div className="flex justify-end">
                <div>
                  <DatePickerWithRange onDateChange={(date) => setDateRange(date)} />
                </div>
              </div>
            </Stack>
          </Stack>
          <div className="pb-5">
            {isLoadingAnalysisHistory ? (
              <div className="flex justify-center items-center h-[40svh]">
                <Loader />
              </div>
            ) : analysisHistory && allAnalysisHistory && allAnalysisHistory.length > 0 ? (
              <div>
                <div className="lg:hidden flex flex-col gap-2">
                  {allAnalysisHistory.map((item) => (
                    <AnalysisItem key={item.taskId} data={item} />
                  ))}
                  <div ref={scrollRef} className="flex justify-center w-full min-h-14 h-14">
                    {isFetchingNextPage && <Loader />}
                  </div>
                </div>
                <div className="hidden lg:block">
                  <DataTable data={allAnalysisHistory} columns={historyColumns} />
                </div>
              </div>
            ) : allAnalysisHistory.length === 0 ? (
              <div className="flex justify-center items-center h-[40svh]">
                <p className="text-sm text-thememutedforeground">No analysis history found</p>
              </div>
            ) : null}
          </div>
        </Stack>
      </AccessControl>
    </>
  )
}

function AnalysisItem({ data }: { data: HistoryType }) {
  const [analysisOpen, setAnalysisOpen] = useState<boolean>(false)

  return (
    <div className="rounded-md border p-3.5 space-y-3">
      {analysisOpen && data && data.analysis && (
        <InspirationAnalysisDetails
          open={analysisOpen}
          onOpenChange={() => setAnalysisOpen(false)}
          data={data.analysis}
          showUseThisFormatButton={false}
        />
      )}
      <div className="flex gap-5 justify-between items-center">
        <div className="p-0.5 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500">
          <div className="rounded-md px-3 py-1 bg-white font-semibold text-black text-sm">
            Score: <span className="text-orange-600">{data.score}</span>
          </div>
        </div>
        <div>
          <div>
            <Badge variant={'outline'} className={'flex gap-1'}>
              <span
                className={cn(
                  {
                    'bg-green-500': data.score >= 70,
                    'bg-orange-500': data.score >= 50 && data.score < 70,
                    'bg-red-500': data.score < 50,
                  },
                  'rounded-full w-1.5 h-1.5',
                )}
              ></span>{' '}
              <span className="text-xs">{data.score >= 70 ? 'Strong' : data.score >= 50 ? 'Medium' : 'Weak'}</span>
            </Badge>
          </div>
        </div>
      </div>
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-thememutedforeground">
            <span className="font-semibold">Date Analyzed:</span> {moment(data.createdAt).format('MMM D, YYYY')}
          </p>
        </div>
        <div>
          <p className="text-base text-thememutedforeground">
            Grade:{' '}
            <span className="font-bold">{data.analysis.analysis.coherence_evaluation?.overall_score?.grade}</span>
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <span>
          <img
            src={data.images[0].fcImageUrl}
            alt="ad"
            width={100}
            height={100}
            className="rounded-md object-cover h-[12rem] w-full"
          />
        </span>
        <span>
          <img
            src={data.images[1].fcImageUrl}
            alt="landing-page"
            width={100}
            height={100}
            className="rounded-md object-cover h-[12rem] w-full"
          />
        </span>
      </div>
      <div className="mt-3">
        <Button
          onClick={() => {
            setAnalysisOpen(true)
          }}
          variant={'outline'}
          size={'sm'}
          className="w-full"
        >
          View Analysis
        </Button>
      </div>
    </div>
  )
}
